import type { HTMLAttributes } from "react";

import { cn } from "@/lib/utils";

export interface CountBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  /** The number to show. Nullish or `<= 0` renders nothing. */
  count: number | null | undefined;
  /** Values above this render as `${max}+`. */
  max?: number;
  /**
   * Screen-reader text rendered inside the pill after the visible number
   * (e.g. `t('channels.unread', { count })` → "4 unread"). When set, the
   * visible number is `aria-hidden` so assistive tech reads the label alone.
   */
  srLabel?: string;
}

/**
 * A small numeric pill for unread / pending counts (header bell + mail,
 * detail-page tabs, the project sidebar). Generic on purpose: no colour of its
 * own — the surface passes the pair it needs (`bg-header-badge
 * text-header-badge-foreground` on the header, `bg-primary
 * text-primary-foreground` on tabs, `bg-sidebar-cta …` on the sidebar), so the
 * badge stays visible on every theme preset (docs/THEMING.md).
 *
 * Hides at zero: a badge that reads "0" is noise, not information.
 */
export function CountBadge({ count, max = 99, srLabel, className, ...rest }: CountBadgeProps) {
  if (!count || count <= 0) return null;
  const visible = count > max ? `${max}+` : count;
  return (
    <span
      className={cn(
        "inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-normal leading-none tabular-nums",
        className,
      )}
      {...rest}
    >
      {srLabel ? (
        <>
          <span aria-hidden="true">{visible}</span>
          <span className="sr-only">{srLabel}</span>
        </>
      ) : (
        visible
      )}
    </span>
  );
}
